/**
 * Live Feature Monitor Dashboard
 *
 * Real-time monitoring of all application features:
 * - Live health checks (actual API endpoint pings)
 * - Activity stream (recent operations)
 * - Performance metrics (response times, error rates)
 * - Auto-refresh with configurable interval
 *
 * VERSION: 3.28
 * LAST UPDATED: 2025-12-24
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Activity, RefreshCw, Pause, Play, CheckCircle, XCircle, AlertTriangle,
  Clock, Zap, BarChart3, List, ChevronDown, ChevronUp, Minus, Filter,
  FileJson, FileText, Box
} from 'lucide-react';
import { monitorAPI } from '../../api';
import { getAuthHeaders } from '../../api/core.js';

// Monitor version metadata
const MONITOR_VERSION = '3.28';
const MONITOR_LAST_UPDATED = '2025-12-24';

// Status configuration (icônes/couleurs uniquement — libellés résolus au rendu via t())
const STATUS_ICON_CONFIG = {
  healthy: { color: 'text-green-400', bg: 'bg-green-500/20', icon: CheckCircle },
  unhealthy: { color: 'text-red-400', bg: 'bg-red-500/20', icon: XCircle },
  slow: { color: 'text-yellow-400', bg: 'bg-yellow-500/20', icon: AlertTriangle },
  unknown: { color: 'text-slate-400', bg: 'bg-slate-500/20', icon: Minus },
};

// Refresh interval options (ms) — libellés résolus au rendu via t()
const REFRESH_OPTION_VALUES = [
  { value: 5000, key: 's5' },
  { value: 10000, key: 's10' },
  { value: 30000, key: 's30' },
  { value: 60000, key: 'm1' },
  { value: 0, key: 'manual' },
];

export function FeatureMonitorTab() {
  const { t } = useTranslation();
  // State
  const [health, setHealth] = useState({ summary: null, features: [] });
  const [activity, setActivity] = useState({ activities: [], byFeature: {} });
  const [metrics, setMetrics] = useState({ summary: null, features: [] });
  const [loading, setLoading] = useState({ health: true, activity: true, metrics: true });
  const [error, setError] = useState(null);

  // UI State
  const [refreshInterval, setRefreshInterval] = useState(10000);
  const [isPaused, setIsPaused] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [activeTab, setActiveTab] = useState('health'); // 'health', 'activity', 'metrics'
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectedFeature] = useState(null);
  const [activityFilter, setActivityFilter] = useState('all'); // 'all', 'errors', 'slow'

  // Refs
  const refreshTimerRef = useRef(null);
  const healthCheckInProgress = useRef(false);

  // Download snapshot
  const downloadSnapshot = async (format) => {
    try {
      const url = `/api/v1/monitor/snapshot?format=${format}`;
      const response = await fetch(url, { headers: getAuthHeaders() });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `snapshot-${timestamp}.${format === 'md' ? 'md' : 'json'}`;

      if (format === 'json') {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(downloadUrl);
      } else {
        const text = await response.text();
        const blob = new Blob([text], { type: 'text/markdown' });
        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(downloadUrl);
      }
    } catch (err) {
      console.error('Snapshot download error:', err);
      setError(t('settings.monitor.errors.snapshotDownload', { message: err.message }));
    }
  };

  // Download tech stack snapshot
  const downloadTechStackSnapshot = async (format) => {
    try {
      const url = `/api/v1/monitor/tech-stack-snapshot?format=${format}`;
      const response = await fetch(url, { headers: getAuthHeaders() });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `tech-stack-${timestamp}.${format === 'md' ? 'md' : 'json'}`;

      if (format === 'json') {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(downloadUrl);
      } else {
        const text = await response.text();
        const blob = new Blob([text], { type: 'text/markdown' });
        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(downloadUrl);
      }
    } catch (err) {
      console.error('Tech stack snapshot download error:', err);
      setError(t('settings.monitor.errors.techStackDownload', { message: err.message }));
    }
  };

  // Fetch health data
  const fetchHealth = useCallback(async () => {
    if (healthCheckInProgress.current) return;
    healthCheckInProgress.current = true;

    try {
      setLoading(prev => ({ ...prev, health: true }));
      const response = await monitorAPI.getHealth();
      if (response.ok) {
        setHealth(response.data);
        setLastRefresh(new Date());
      }
    } catch (err) {
      console.error('Health fetch error:', err);
      setError(t('settings.monitor.errors.healthFetch'));
    } finally {
      setLoading(prev => ({ ...prev, health: false }));
      healthCheckInProgress.current = false;
    }
  }, []);

  // Fetch activity data
  const fetchActivity = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, activity: true }));
      const response = await monitorAPI.getActivity({ limit: 100 });
      if (response.ok) {
        setActivity(response.data);
      }
    } catch (err) {
      console.error('Activity fetch error:', err);
    } finally {
      setLoading(prev => ({ ...prev, activity: false }));
    }
  }, []);

  // Fetch metrics data
  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, metrics: true }));
      const response = await monitorAPI.getMetrics({ hours: 24 });
      if (response.ok) {
        setMetrics(response.data);
      }
    } catch (err) {
      console.error('Metrics fetch error:', err);
    } finally {
      setLoading(prev => ({ ...prev, metrics: false }));
    }
  }, []);

  // Combined refresh
  const refresh = useCallback(async () => {
    if (isPaused) return;
    await Promise.all([fetchHealth(), fetchActivity(), fetchMetrics()]);
  }, [fetchHealth, fetchActivity, fetchMetrics, isPaused]);

  // Initial load
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh
  useEffect(() => {
    if (refreshInterval > 0 && !isPaused) {
      refreshTimerRef.current = setInterval(refresh, refreshInterval);
    }
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [refreshInterval, isPaused, refresh]);

  // Toggle pause
  const togglePause = () => {
    setIsPaused(prev => !prev);
  };

  // Manual refresh
  const manualRefresh = () => {
    refresh();
  };

  // Toggle group expansion
  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  // Group features by nav_group
  const groupedFeatures = health.features.reduce((acc, feature) => {
    const group = feature.nav_group || 'other';
    if (!acc[group]) acc[group] = [];
    acc[group].push(feature);
    return acc;
  }, {});

  // Get status for a feature
  const getStatus = (feature) => {
    if (feature.is_healthy === undefined) return 'unknown';
    if (!feature.is_healthy) return 'unhealthy';
    if (feature.response_time_ms > 1000) return 'slow';
    return 'healthy';
  };

  // Filter activities
  const filteredActivities = activity.activities ? activity.activities.filter(a => {
    if (activityFilter === 'errors') return a.status_code >= 400;
    if (activityFilter === 'slow') return a.response_time_ms > 500;
    if (selectedFeature) return a.feature_code === selectedFeature;
    return true;
  }) : [];

  // Summary stats content
  const summaryStatsContent = (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <div className="bg-slate-800 rounded-lg p-4">
        <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
          <Activity size={16} />
          {t('settings.monitor.summary.monitored')}
        </div>
        <div className="text-2xl font-bold text-white">{health.summary?.total || 0}</div>
      </div>
      <div className="bg-slate-800 rounded-lg p-4">
        <div className="flex items-center gap-2 text-green-400 text-sm mb-1">
          <CheckCircle size={16} />
          {t('settings.monitor.summary.healthy')}
        </div>
        <div className="text-2xl font-bold text-green-400">{health.summary?.healthy || 0}</div>
      </div>
      <div className="bg-slate-800 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-400 text-sm mb-1">
          <XCircle size={16} />
          {t('settings.monitor.summary.unhealthy')}
        </div>
        <div className="text-2xl font-bold text-red-400">{health.summary?.unhealthy || 0}</div>
      </div>
      <div className="bg-slate-800 rounded-lg p-4">
        <div className="flex items-center gap-2 text-blue-400 text-sm mb-1">
          <Zap size={16} />
          {t('settings.monitor.summary.avgTime')}
        </div>
        <div className="text-2xl font-bold text-blue-400">
          {health.summary?.avg_response_time_ms || 0}ms
        </div>
      </div>
    </div>
  );

  // Health panel content (inlined to avoid remount issues)
  const healthPanelContent = (
    <div className="space-y-4">
      {Object.entries(groupedFeatures).map(([group, features]) => (
        <div key={group} className="bg-slate-800 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => toggleGroup(group)}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <span className="font-medium text-white">
                {t(`settings.monitor.groupLabels.${group}`, { defaultValue: group })}
              </span>
              <span className="text-sm text-slate-400">
                {t('settings.monitor.health.featuresCount', { count: features.length })}
              </span>
              <div className="flex gap-1">
                {features.filter(f => getStatus(f) === 'healthy').length > 0 && (
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                )}
                {features.filter(f => getStatus(f) === 'unhealthy').length > 0 && (
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                )}
                {features.filter(f => getStatus(f) === 'slow').length > 0 && (
                  <span className="w-2 h-2 rounded-full bg-yellow-500" />
                )}
              </div>
            </div>
            {expandedGroups[group] ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
          </button>

          {expandedGroups[group] && (
            <div className="border-t border-slate-700">
              {features.map(feature => {
                const status = getStatus(feature);
                const config = STATUS_ICON_CONFIG[status];
                const StatusIcon = config.icon;

                return (
                  <div
                    key={feature.feature_code}
                    className="px-4 py-3 flex items-center justify-between border-b border-slate-700/50 last:border-b-0 hover:bg-slate-700/50"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-1.5 rounded ${config.bg}`}>
                        <StatusIcon size={16} className={config.color} />
                      </div>
                      <div>
                        <div className="font-medium text-white">{feature.feature_name}</div>
                        <div className="text-xs text-slate-400">{feature.endpoint}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className={`text-sm font-medium ${config.color}`}>
                          {feature.response_time_ms !== null ? `${feature.response_time_ms}ms` : '-'}
                        </div>
                        <div className="text-xs text-slate-500">
                          {feature.status_code || '-'}
                        </div>
                      </div>
                      {feature.error_message && (
                        <div className="text-xs text-red-400 max-w-[200px] truncate" title={feature.error_message}>
                          {feature.error_message}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
      {Object.keys(groupedFeatures).length === 0 && (
        <div className="text-center text-slate-400 py-8">
          {t('settings.monitor.health.empty')}
        </div>
      )}
    </div>
  );

  // Activity panel content
  const activityPanelContent = (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-slate-400" />
          <select
            value={activityFilter}
            onChange={(e) => setActivityFilter(e.target.value)}
            className="bg-slate-700 border border-slate-600 rounded px-3 py-1.5 text-sm text-white"
          >
            <option value="all">{t('settings.monitor.activity.filterAll')}</option>
            <option value="errors">{t('settings.monitor.activity.filterErrors')}</option>
            <option value="slow">{t('settings.monitor.activity.filterSlow')}</option>
          </select>
        </div>
        <div className="text-sm text-slate-400">
          {t('settings.monitor.activity.countLabel', { filtered: filteredActivities.length, total: activity.total || 0 })}
        </div>
      </div>

      {/* Activity list */}
      <div className="bg-slate-800 rounded-lg overflow-hidden">
        <div className="max-h-[500px] overflow-y-auto">
          {filteredActivities.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              {t('settings.monitor.activity.empty')}
            </div>
          ) : (
            filteredActivities.map((act, idx) => (
              <div
                key={act.id || idx}
                className="px-4 py-3 border-b border-slate-700/50 last:border-b-0 hover:bg-slate-700/30"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      act.method === 'GET' ? 'bg-blue-500/20 text-blue-400' :
                      act.method === 'POST' ? 'bg-green-500/20 text-green-400' :
                      act.method === 'PUT' ? 'bg-yellow-500/20 text-yellow-400' :
                      act.method === 'DELETE' ? 'bg-red-500/20 text-red-400' :
                      'bg-slate-500/20 text-slate-400'
                    }`}>
                      {act.method}
                    </span>
                    <span className="text-white font-mono text-sm">{act.endpoint}</span>
                    {act.feature_code && (
                      <span className="px-2 py-0.5 bg-slate-600 rounded text-xs text-slate-300">
                        {act.feature_code}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className={`font-medium ${
                      act.status_code < 300 ? 'text-green-400' :
                      act.status_code < 400 ? 'text-blue-400' :
                      act.status_code < 500 ? 'text-yellow-400' :
                      'text-red-400'
                    }`}>
                      {act.status_code}
                    </span>
                    <span className={`${act.response_time_ms > 500 ? 'text-yellow-400' : 'text-slate-400'}`}>
                      {act.response_time_ms}ms
                    </span>
                    <span className="text-slate-500">
                      {new Date(act.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                </div>
                {act.error_message && (
                  <div className="mt-2 text-xs text-red-400 bg-red-500/10 rounded p-2">
                    {act.error_message}
                  </div>
                )}
                {act.user_email && (
                  <div className="mt-1 text-xs text-slate-500">
                    {t('settings.monitor.activity.userLabel', { email: act.user_email })}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );

  // Metrics panel content
  const metricsPanelContent = (
    <div className="space-y-4">
      {/* Global metrics */}
      {metrics.summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-slate-800 rounded-lg p-4">
            <div className="text-slate-400 text-sm mb-1">{t('settings.monitor.metrics.totalRequests')}</div>
            <div className="text-xl font-bold text-white">{metrics.summary.total_requests}</div>
          </div>
          <div className="bg-slate-800 rounded-lg p-4">
            <div className="text-slate-400 text-sm mb-1">{t('settings.monitor.metrics.errorRate')}</div>
            <div className={`text-xl font-bold ${metrics.summary.error_rate > 5 ? 'text-red-400' : 'text-green-400'}`}>
              {metrics.summary.error_rate}%
            </div>
          </div>
          <div className="bg-slate-800 rounded-lg p-4">
            <div className="text-slate-400 text-sm mb-1">{t('settings.monitor.metrics.avgTime')}</div>
            <div className="text-xl font-bold text-blue-400">
              {metrics.summary.avg_response_time_ms || 0}ms
            </div>
          </div>
          <div className="bg-slate-800 rounded-lg p-4">
            <div className="text-slate-400 text-sm mb-1">{t('settings.monitor.metrics.requestsPerHour')}</div>
            <div className="text-xl font-bold text-white">{metrics.summary.requests_per_hour}</div>
          </div>
          <div className="bg-slate-800 rounded-lg p-4">
            <div className="text-slate-400 text-sm mb-1">{t('settings.monitor.metrics.features')}</div>
            <div className="text-xl font-bold text-white">
              {metrics.summary.healthy_features}/{metrics.summary.features_monitored}
            </div>
          </div>
        </div>
      )}

      {/* Per-feature metrics */}
      <div className="bg-slate-800 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-700">
          <h3 className="font-medium text-white">{t('settings.monitor.metrics.perFeatureTitle')}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-700/50">
                <th className="px-4 py-2 text-left text-sm text-slate-300">{t('settings.monitor.metrics.columns.feature')}</th>
                <th className="px-4 py-2 text-right text-sm text-slate-300">{t('settings.monitor.metrics.columns.requests')}</th>
                <th className="px-4 py-2 text-right text-sm text-slate-300">{t('settings.monitor.metrics.columns.errors')}</th>
                <th className="px-4 py-2 text-right text-sm text-slate-300">{t('settings.monitor.metrics.columns.errorRate')}</th>
                <th className="px-4 py-2 text-right text-sm text-slate-300">{t('settings.monitor.metrics.columns.avg')}</th>
                <th className="px-4 py-2 text-right text-sm text-slate-300">{t('settings.monitor.metrics.columns.p95')}</th>
                <th className="px-4 py-2 text-right text-sm text-slate-300">{t('settings.monitor.metrics.columns.status')}</th>
              </tr>
            </thead>
            <tbody>
              {metrics.features?.length > 0 ? metrics.features.map(feature => (
                <tr key={feature.feature_code} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                  <td className="px-4 py-3 text-white font-medium">{feature.feature_code}</td>
                  <td className="px-4 py-3 text-right text-slate-300">{feature.total_requests}</td>
                  <td className="px-4 py-3 text-right text-red-400">{feature.failed_requests}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={feature.error_rate > 5 ? 'text-red-400' : 'text-green-400'}>
                      {feature.error_rate}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-slate-300">
                    {feature.avg_response_time_ms || '-'}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-300">
                    {feature.p95_response_time_ms || '-'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {feature.is_healthy ? (
                      <CheckCircle size={16} className="text-green-400 inline" />
                    ) : (
                      <XCircle size={16} className="text-red-400 inline" />
                    )}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                    {t('settings.monitor.metrics.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <Activity className="text-blue-500" />
            {t('settings.monitor.title')}
            <span className="text-xs font-normal text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
              v{MONITOR_VERSION}
            </span>
          </h1>
          <p className="text-slate-500 mt-1">
            {t('settings.monitor.subtitle')}
            <span className="ml-2 text-xs text-slate-400">
              • {t('settings.monitor.updatedOn', { date: MONITOR_LAST_UPDATED })}
            </span>
          </p>
        </div>

        <div className="flex items-center gap-4">
          {/* Last refresh */}
          {lastRefresh && (
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <Clock size={14} />
              {t('settings.monitor.lastRefresh', { time: lastRefresh.toLocaleTimeString() })}
            </div>
          )}

          {/* Snapshot download buttons */}
          <div className="flex items-center gap-1 border-r border-slate-300 pr-4">
            <span className="text-xs text-slate-500 mr-1">{t('settings.monitor.downloadHealthLabel')}</span>
            <button
              onClick={() => downloadSnapshot('json')}
              className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600"
              title={t('settings.monitor.downloadHealthJsonTitle')}
            >
              <FileJson size={18} />
            </button>
            <button
              onClick={() => downloadSnapshot('md')}
              className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600"
              title={t('settings.monitor.downloadHealthMdTitle')}
            >
              <FileText size={18} />
            </button>
          </div>

          {/* Tech Stack snapshot buttons */}
          <div className="flex items-center gap-1 border-r border-slate-300 pr-4">
            <span className="text-xs text-slate-500 mr-1">{t('settings.monitor.downloadStackLabel')}</span>
            <button
              onClick={() => downloadTechStackSnapshot('json')}
              className="p-2 rounded-lg bg-purple-100 hover:bg-purple-200 text-purple-600"
              title={t('settings.monitor.downloadStackJsonTitle')}
            >
              <Box size={18} />
            </button>
            <button
              onClick={() => downloadTechStackSnapshot('md')}
              className="p-2 rounded-lg bg-purple-100 hover:bg-purple-200 text-purple-600"
              title={t('settings.monitor.downloadStackMdTitle')}
            >
              <FileText size={18} />
            </button>
          </div>

          {/* Refresh interval */}
          <select
            value={refreshInterval}
            onChange={(e) => setRefreshInterval(parseInt(e.target.value))}
            className="bg-white border border-slate-300 rounded px-3 py-2 text-sm text-slate-700"
          >
            {REFRESH_OPTION_VALUES.map(opt => (
              <option key={opt.value} value={opt.value}>{t(`settings.monitor.refreshOptions.${opt.key}`)}</option>
            ))}
          </select>

          {/* Pause/Play */}
          <button
            onClick={togglePause}
            className={`p-2 rounded-lg ${isPaused ? 'bg-green-500 hover:bg-green-600' : 'bg-yellow-500 hover:bg-yellow-600'} text-white`}
            title={isPaused ? t('settings.monitor.resumeTitle') : t('settings.monitor.pauseTitle')}
          >
            {isPaused ? <Play size={18} /> : <Pause size={18} />}
          </button>

          {/* Manual refresh */}
          <button
            onClick={manualRefresh}
            disabled={loading.health}
            className="p-2 rounded-lg bg-blue-500 hover:bg-blue-600 disabled:bg-slate-400 text-white"
            title={t('settings.monitor.manualRefreshTitle')}
          >
            <RefreshCw size={18} className={loading.health ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-300 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Summary stats */}
      {summaryStatsContent}

      {/* Tabs */}
      <div className="flex border-b border-slate-300 mb-6">
        <button
          onClick={() => setActiveTab('health')}
          className={`px-4 py-3 flex items-center gap-2 border-b-2 transition-colors ${
            activeTab === 'health'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <CheckCircle size={18} />
          {t('settings.monitor.tabs.health')}
        </button>
        <button
          onClick={() => setActiveTab('activity')}
          className={`px-4 py-3 flex items-center gap-2 border-b-2 transition-colors ${
            activeTab === 'activity'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <List size={18} />
          {t('settings.monitor.tabs.activity')}
          {activity.activities?.length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-slate-200 rounded-full text-xs">
              {activity.activities.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('metrics')}
          className={`px-4 py-3 flex items-center gap-2 border-b-2 transition-colors ${
            activeTab === 'metrics'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <BarChart3 size={18} />
          {t('settings.monitor.tabs.metrics')}
        </button>
      </div>

      {/* Content */}
      {activeTab === 'health' && healthPanelContent}
      {activeTab === 'activity' && activityPanelContent}
      {activeTab === 'metrics' && metricsPanelContent}
    </div>
  );
}

export default FeatureMonitorTab;
